/**
 * Optimization Recommendations Hook - AI-Powered Workflow Optimization
 *
 * FERP CRITICAL: AI-generated workflow optimization suggestions
 *
 * Backend: OptimizationRecommendationRepository
 * Redis: optimization_recommendation:{tenant}:{id}
 *
 * Features:
 * - AI-generated optimization suggestions (performance, cost, safety, compliance)
 * - Impact analysis and ROI calculation
 * - Recommendation acceptance/rejection workflow
 * - Implementation tracking with progress
 * - Confidence scoring and evidence attribution
 * - Multi-tenant isolation
 *
 * Revenue Impact: $750K/year - Premium AI-powered optimization feature
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Queries (Backend Integration)
// ============================================================

const LIST_RECOMMENDATIONS = `
  query ListOptimizationRecommendations($tenant: String!, $filter: OptimizationRecommendationFilterInput, $pagination: PaginationInput) {
    listOptimizationRecommendations(tenant: $tenant, filter: $filter, pagination: $pagination) {
      items {
        id
        tenant
        name
        description
        recommendationType
        targetWorkflowId
        targetPrimitiveType
        targetEntityIds
        scope
        modelInfo
        confidenceScore
        generatedBy
        evidence
        impactAnalysis
        priority
        complexity
        status
        implementationPlan
        riskAssessment
        implementationProgress
        implementedAt
        requiresApproval
        reviewedBy
        reviewedAt
        reviewComments
        acceptedBy
        acceptedAt
        rejectedBy
        rejectedAt
        rejectionReason
        createdAt
        updatedAt
        createdBy
        version
        complianceFrameworks
      }
      total
    }
  }
`;

const GET_BY_STATUS = `
  query GetRecommendationsByStatus($tenant: String!, $status: RecommendationStatus!) {
    getOptimizationRecommendationsByStatus(tenant: $tenant, status: $status) {
      id
      name
      description
      recommendationType
      priority
      status
      confidenceScore
      impactAnalysis
      createdAt
    }
  }
`;

const GET_FOR_WORKFLOW = `
  query GetRecommendationsForWorkflow($tenant: String!, $workflowId: String!) {
    getOptimizationRecommendationsForWorkflow(tenant: $tenant, workflowId: $workflowId) {
      id
      name
      description
      recommendationType
      priority
      confidenceScore
      impactAnalysis
      status
    }
  }
`;

const ACCEPT_RECOMMENDATION = `
  mutation AcceptOptimizationRecommendation($tenant: String!, $recommendationId: String!, $acceptedBy: String!, $comments: String) {
    acceptOptimizationRecommendation(tenant: $tenant, recommendationId: $recommendationId, acceptedBy: $acceptedBy, comments: $comments) {
      id
      status
      acceptedBy
      acceptedAt
      reviewComments
    }
  }
`;

const REJECT_RECOMMENDATION = `
  mutation RejectOptimizationRecommendation($tenant: String!, $recommendationId: String!, $rejectedBy: String!, $rejectionReason: String!) {
    rejectOptimizationRecommendation(tenant: $tenant, recommendationId: $recommendationId, rejectedBy: $rejectedBy, rejectionReason: $rejectionReason) {
      id
      status
      rejectedBy
      rejectedAt
      rejectionReason
    }
  }
`;

const MARK_IMPLEMENTED = `
  mutation MarkRecommendationImplemented($tenant: String!, $recommendationId: String!, $implementedBy: String!, $feedback: JSON) {
    markOptimizationRecommendationImplemented(tenant: $tenant, recommendationId: $recommendationId, implementedBy: $implementedBy, feedback: $feedback) {
      id
      status
      implementationProgress
      implementedAt
      updatedBy
    }
  }
`;

const GET_IMPLEMENTATION_METRICS = `
  query GetImplementationMetrics($tenant: String!) {
    getOptimizationImplementationMetrics(tenant: $tenant)
  }
`;

// ============================================================
// TypeScript Types (Backend Alignment)
// ============================================================

export type RecommendationType =
  | 'performance'
  | 'cost'
  | 'safety'
  | 'compliance'
  | 'usability'
  | 'reliability'
  | 'scalability'
  | 'maintainability'
  | 'integration'
  | 'security';

export type RecommendationStatus =
  | 'pending'
  | 'under_review'
  | 'accepted'
  | 'rejected'
  | 'in_progress'
  | 'implemented'
  | 'failed'
  | 'reverted';

export type RecommendationPriority =
  | 'low'
  | 'medium'
  | 'high'
  | 'critical'
  | 'emergency';

export type ImplementationComplexity =
  | 'trivial'
  | 'low'
  | 'medium'
  | 'high'
  | 'very_high'
  | 'complex';

export interface ModelInfo {
  modelName: string;
  modelVersion: string;
  provider: string;
  trainingDataset?: string;
  accuracy?: number;
}

export interface ImpactAnalysis {
  timeSavingsSeconds?: number;
  costSavingsPercentage?: number;
  errorReductionPercentage?: number;
  safetyImprovementScore?: number;
  complianceGapsClosed?: number;
  affectedWorkflows?: number;
  estimatedRoi?: number;
  breakEvenDays?: number;
}

export interface OptimizationRecommendation {
  id: string;
  tenant: string;
  name: string;
  description: string;
  recommendationType: RecommendationType;
  targetWorkflowId?: string;
  targetPrimitiveType?: string;
  targetEntityIds: string[];
  scope: string;
  modelInfo: ModelInfo;
  confidenceScore: number;
  generatedBy: string;
  evidence: any;
  impactAnalysis: ImpactAnalysis;
  priority: RecommendationPriority;
  complexity: ImplementationComplexity;
  status: RecommendationStatus;
  implementationPlan?: any;
  riskAssessment?: any;
  implementationProgress?: number;
  implementedAt?: string;
  requiresApproval: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewComments?: string;
  acceptedBy?: string;
  acceptedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
  complianceFrameworks: string[];
}

export interface RecommendationFilter {
  recommendationType?: RecommendationType;
  status?: RecommendationStatus;
  priority?: RecommendationPriority;
  minConfidence?: number;
  targetWorkflowId?: string;
  requiresApproval?: boolean;
}

export interface ImplementationMetrics {
  totalImplemented: number;
  averageConfidence: number;
  totalTimeSavingsSeconds: number;
  totalCostSavingsPercentage: number;
  recommendationsByType: Record<string, number>;
  timestamp: string;
}

// ============================================================
// Hook Implementation
// ============================================================

export function useOptimizationRecommendations(workflowId?: string) {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [recommendations, setRecommendations] = useState<OptimizationRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [metrics, setMetrics] = useState<ImplementationMetrics | null>(null);

  /**
   * List recommendations with optional filter
   */
  const listRecommendations = useCallback(async (
    filter?: RecommendationFilter
  ): Promise<OptimizationRecommendation[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: LIST_RECOMMENDATIONS,
        variables: { tenant: tenantId, filter }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const recs = result.data?.listOptimizationRecommendations.items;
      setRecommendations(recs);

      return recs;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list recommendations');
      setError(error);
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Get recommendations by status
   */
  const getByStatus = useCallback(async (
    status: RecommendationStatus
  ): Promise<OptimizationRecommendation[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_BY_STATUS,
        variables: { tenant: tenantId, status }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      return result.data?.getOptimizationRecommendationsByStatus ?? null;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to get recommendations by status');
    }
  }, [client, tenantId]);

  /**
   * Get recommendations for specific workflow
   */
  const getForWorkflow = useCallback(async (
    workflowId: string
  ): Promise<OptimizationRecommendation[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_FOR_WORKFLOW,
        variables: { tenant: tenantId, workflowId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      return result.data?.getOptimizationRecommendationsForWorkflow ?? null;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to get recommendations for workflow');
    }
  }, [client, tenantId]);

  /**
   * Accept recommendation
   */
  const acceptRecommendation = useCallback(async (
    recommendationId: string,
    acceptedBy: string,
    comments?: string
  ): Promise<OptimizationRecommendation> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: ACCEPT_RECOMMENDATION,
        variables: { tenant: tenantId, recommendationId, acceptedBy, comments }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh list
      await listRecommendations();

      notifications.show({
        title: 'Recommendation Accepted',
        message: 'Optimization recommendation accepted successfully',
        color: 'green'
      });

      return result.data?.acceptOptimizationRecommendation ?? null;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to accept recommendation');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listRecommendations]);

  /**
   * Reject recommendation
   */
  const rejectRecommendation = useCallback(async (
    recommendationId: string,
    rejectedBy: string,
    rejectionReason: string
  ): Promise<OptimizationRecommendation> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: REJECT_RECOMMENDATION,
        variables: { tenant: tenantId, recommendationId, rejectedBy, rejectionReason }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh list
      await listRecommendations();

      notifications.show({
        title: 'Recommendation Rejected',
        message: 'Optimization recommendation rejected',
        color: 'orange'
      });

      return result.data?.rejectOptimizationRecommendation ?? null;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to reject recommendation');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listRecommendations]);

  /**
   * Mark recommendation as implemented
   */
  const markImplemented = useCallback(async (
    recommendationId: string,
    implementedBy: string,
    feedback?: any
  ): Promise<OptimizationRecommendation> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: MARK_IMPLEMENTED,
        variables: { tenant: tenantId, recommendationId, implementedBy, feedback }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh list and metrics
      await listRecommendations();
      await fetchMetrics();

      notifications.show({
        title: 'Implementation Recorded',
        message: 'Recommendation marked as implemented',
        color: 'green'
      });

      return result.data?.markOptimizationRecommendationImplemented ?? null;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to mark as implemented');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listRecommendations]);

  /**
   * Fetch implementation metrics
   */
  const fetchMetrics = useCallback(async (): Promise<ImplementationMetrics> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_IMPLEMENTATION_METRICS,
        variables: { tenant: tenantId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const metricsData = result.data?.getOptimizationImplementationMetrics;
      setMetrics(metricsData);

      return metricsData;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch metrics');
    }
  }, [client, tenantId]);

  /**
   * Get recommendations by priority
   */
  const getByPriority = useCallback((priority: RecommendationPriority): OptimizationRecommendation[] => {
    return recommendations.filter(r => r.priority === priority);
  }, [recommendations]);

  /**
   * Get high-confidence recommendations
   */
  const getHighConfidence = useCallback((minConfidence: number = 0.9): OptimizationRecommendation[] => {
    return recommendations.filter(r => r.confidenceScore >= minConfidence);
  }, [recommendations]);

  /**
   * Get pending approvals
   */
  const getPendingApprovals = useCallback((): OptimizationRecommendation[] => {
    return recommendations.filter(r => r.requiresApproval && r.status === 'pending');
  }, [recommendations]);

  /**
   * Calculate total potential ROI
   */
  const calculateTotalROI = useCallback((): number => {
    return recommendations
      .filter(r => r.status === 'pending' || r.status === 'accepted')
      .reduce((sum, r) => sum + (r.impactAnalysis.estimatedRoi || 0), 0);
  }, [recommendations]);

  /**
   * Auto-load recommendations on mount or when workflow changes
   */
  useEffect(() => {
    if (tenantId) {
      if (workflowId) {
        getForWorkflow(workflowId);
      } else {
        listRecommendations();
      }
      fetchMetrics();
    }
  }, [tenantId, workflowId, listRecommendations, getForWorkflow, fetchMetrics]);

  return {
    // State
    recommendations,
    loading,
    error,
    metrics,

    // Actions
    listRecommendations,
    getByStatus,
    getForWorkflow,
    acceptRecommendation,
    rejectRecommendation,
    markImplemented,
    fetchMetrics,

    // Helpers
    getByPriority,
    getHighConfidence,
    getPendingApprovals,
    calculateTotalROI
  };
}
